import React from "react";
import { Helmet } from "react-helmet";
import { BASE_SITE_URL } from "../../config";
import { SUPPORTED_LANGUAGES } from "../../languages";

type SeoHeadProps = {
  title: string;
  description: string;
  path?: string;
  keywords?: string[];
  imageUrl?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
};

const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description,
  path = "",
  keywords = [],
  imageUrl,
  structuredData,
}) => {
  const normalizedPath = path
    ? path.startsWith("/")
      ? path
      : `/${path}`
    : "";
  const canonicalUrl = `${BASE_SITE_URL}${normalizedPath}`;
  const socialImage = imageUrl || `${BASE_SITE_URL}/favicon.ico`;
  const currentLanguage =
    typeof document !== "undefined" && document.documentElement.lang
      ? document.documentElement.lang
      : "en";

  const ogLocaleMap: Record<string, string> = {
    en: "en_US",
    es: "es_ES",
    zh: "zh_CN",
    fr: "fr_FR",
    de: "de_DE",
    it: "it_IT",
    ru: "ru_RU",
    ko: "ko_KR",
    ja: "ja_JP",
    tr: "tr_TR",
    hi: "hi_IN",
    "pt-BR": "pt_BR",
    id: "id_ID",
    nl: "nl_NL",
    pl: "pl_PL",
  };

  const defaultLocale = ogLocaleMap.en;

  const ogLocale = ogLocaleMap[currentLanguage] || defaultLocale;
  const buildLanguageUrl = (code: string) => {
    if (code === "en") {
      return canonicalUrl;
    }

    const separator = canonicalUrl.includes("?") ? "&" : "?";
    return `${canonicalUrl}${separator}lng=${code}`;
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      {Array.isArray(keywords) && <meta name="keywords" content={keywords.join(", ")} />}
      <meta
        name="robots"
        content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
      />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={socialImage} />
      <meta property="og:site_name" content="Music Tier List" />
      <meta property="og:locale" content={ogLocale} />
      {SUPPORTED_LANGUAGES.filter((lang) => lang.code !== currentLanguage).map(
        (lang) => (
          <meta
            key={`og-alt-${lang.code}`}
            property="og:locale:alternate"
            content={ogLocaleMap[lang.code] || defaultLocale}
          />
        )
      )}

      <link rel="alternate" hrefLang="x-default" href={canonicalUrl} />
      {SUPPORTED_LANGUAGES.map((lang) => (
        <link
          key={`alternate-${lang.code}`}
          rel="alternate"
          hrefLang={lang.code}
          href={buildLanguageUrl(lang.code)}
        />
      ))}

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImage} />
      <meta name="twitter:site" content="@MusicRandomizer" />

      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

export default SeoHead;
